/**
 * Swamithra — the reporting figures.
 *
 * ONE REQUEST, SIX CARDS, AND THE SERVICE DECIDED THAT. `GET
 * /expert/reporting/summary` answers with every figure the dashboard shows;
 * there is no per-card endpoint, and the six this package used to call answered
 * 404. So the summary is fetched once and each card SELECTS its slice through
 * the functions at the foot of this file.
 *
 * WHAT THAT COSTS AGAINST US-01-006.17. The criterion asks that a card whose
 * request fails show an error with a retry while the others still render. With
 * one endpoint, a failed request fails every card — each one shows the error and
 * the retry, and the retry refetches the one request. What still holds card by
 * card is a MALFORMED or partial response: each selector answers null for a
 * section that is not there, so one missing figure does not blank the five that
 * are.
 *
 * EVERY FIGURE IS AN AGGREGATE. No endpoint behind this dashboard returns a row
 * identifying a user, a query or a mobile number (US-01-006.20), and none of the
 * schemas below has a field that could carry one.
 */

import { z } from "zod"

import {
	emptyCategoryCounts,
	QUERY_CATEGORIES,
	QUERY_CATEGORY_LABELS,
	type QueryCategory,
	queryCategorySchema,
} from "./categories"
import { USER_TYPES, type UserType } from "./enums"

/**
 * The counts that make up the status-split card. Sums to `totalQueries`.
 *
 * KEYED BY THE WIRE ENUM, which is the one object in this package whose keys are
 * values rather than field names. ../api/client rewrites response field names
 * into this package's convention and cannot tell the difference, so `in_progress`
 * arrives as `inProgress`; both spellings are read here so the card counts the
 * same either way and every consumer keeps indexing by `WORK_STATUSES`.
 */
export const statusCountsSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		return { ...record, in_progress: record.in_progress ?? record.inProgress }
	},
	z.object({
		open: z.number().int().nonnegative(),
		in_progress: z.number().int().nonnegative(),
		answered: z.number().int().nonnegative(),
		closed: z.number().int().nonnegative(),
	}),
)

export type StatusCounts = z.infer<typeof statusCountsSchema>

export function totalOf(counts: StatusCounts): number {
	return counts.open + counts.in_progress + counts.answered + counts.closed
}

/** Total queries plus the four-way split, for the selected window. */
export const queryVolumeSchema = z.object({
	totalQueries: z.number().int().nonnegative(),
	statusCounts: statusCountsSchema,
	/** Echoed so a card can label the window it is actually showing. */
	windowLabel: z.string(),
})

export type QueryVolume = z.infer<typeof queryVolumeSchema>

/**
 * One average-response-time figure.
 *
 * `averageHours` is NULL, not 0, when no query received its first response
 * inside the window. Zero hours and no data are different facts, and the
 * criteria treat them differently in two places: the card shows an explicit
 * 'no data yet' state (US-01-006.6) and the user-side banner is not rendered at
 * all (US-00-002.5).
 */
export const responseTimeFigureSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		// The service names the figure `mean_hours` and states emptiness with
		// `has_data`; this package has always called them `averageHours` and
		// `sampleSize`. Both are read, so an empty window is an empty window
		// whichever way it is said — and `has_data: false` never becomes a
		// confident zero hours (US-00-002.5, US-01-006.6).
		const average = record.averageHours ?? record.meanHours ?? null
		const hasData = record.hasData
		return {
			...record,
			averageHours: hasData === false ? null : average,
			sampleSize:
				record.sampleSize ??
				record.samples ??
				record.sampleCount ??
				(hasData === true && average !== null ? 1 : 0),
		}
	},
	z.object({
		averageHours: z.number().nonnegative().nullable(),
		/** Queries the mean is over. Zero means the figure is absent, not zero. */
		sampleSize: z.number().int().nonnegative(),
		windowDays: z.number().int().positive().optional(),
	}),
)

export type ResponseTimeFigure = z.output<typeof responseTimeFigureSchema>

/**
 * The shared computation, served by ONE endpoint to both the reporting dashboard
 * and the user's create-query banner (US-01-006.18). Two endpoints would produce
 * two numbers that disagree by a rounding rule within a month.
 */
export const responseTimesSchema = z.preprocess(
	value => {
		if (value === null || typeof value !== "object") return value
		const record = value as Record<string, unknown>
		// `seven_day` / `thirty_day` is the other spelling of the same two
		// windows. Reading both keeps the create-query banner and the reporting
		// card on the identical figure (US-01-006.18) whichever the service uses.
		return {
			...record,
			last7Days: record.last7Days ?? record.sevenDay ?? record.last7Day,
			last30Days: record.last30Days ?? record.thirtyDay ?? record.last30Day,
		}
	},
	z.object({
		last7Days: responseTimeFigureSchema,
		last30Days: responseTimeFigureSchema,
	}),
)

export type ResponseTimes = z.output<typeof responseTimesSchema>

/** True when the create-query banner should be omitted entirely (US-00-002.5). */
export function hasResponseTimeFigure(figure: ResponseTimeFigure): boolean {
	return figure.averageHours !== null && figure.sampleSize > 0
}

export const vettingVolumeSchema = z.object({
	total: z.number().int().nonnegative(),
	statusCounts: statusCountsSchema,
	windowLabel: z.string(),
})

export type VettingVolume = z.infer<typeof vettingVolumeSchema>

/**
 * Satisfaction, with the denominator.
 *
 * A percentage without the number of responses behind it is how "100%
 * satisfied" gets reported off a single rating (US-01-006.8).
 */
export const satisfactionSchema = z.object({
	responseCount: z.number().int().nonnegative(),
	satisfiedPercent: z.number().min(0).max(100).nullable(),
	notSatisfiedPercent: z.number().min(0).max(100).nullable(),
	windowLabel: z.string(),
})

export type Satisfaction = z.infer<typeof satisfactionSchema>

/**
 * Query volume by category.
 *
 * The server sends only the categories it has counts for; the client fills the
 * rest with zeros through `emptyCategoryCounts`, because all seven must appear
 * every time (US-01-006.9).
 */
export const categoryVolumeSchema = z.object({
	counts: z.array(
		z.object({
			category: queryCategorySchema,
			count: z.number().int().nonnegative(),
		}),
	),
	windowLabel: z.string(),
})

export type CategoryVolume = z.infer<typeof categoryVolumeSchema>

/**
 * The chart's series: all seven categories, in taxonomy order, zero-filled.
 *
 * Here rather than in the chart component because US-01-006.9 is a rule about
 * the DATA, not about a chart library — and a component that has to remember to
 * zero-fill is a component that will forget the week a category has no queries.
 */
export function categorySeries(
	volume: CategoryVolume,
): readonly { category: QueryCategory; label: string; count: number }[] {
	const counts = emptyCategoryCounts()
	for (const entry of volume.counts) {
		counts[entry.category] = entry.count
	}
	return QUERY_CATEGORIES.map(category => ({
		category,
		label: QUERY_CATEGORY_LABELS[category],
		count: counts[category],
	}))
}

/**
 * Registered users.
 *
 * ALL-TIME regardless of the selected window, and labelled as such
 * (US-01-006.11) — which is why this schema has no window label to render.
 */
export const registeredUsersSchema = z.object({
	total: z.number().int().nonnegative(),
	byUserType: z.object({
		professional: z.number().int().nonnegative(),
		individual: z.number().int().nonnegative(),
	}),
	/** Echoed by the service as `all_time`, and the card says so (US-01-006.11). */
	scope: z.string().default("all_time"),
})

export type RegisteredUsers = z.infer<typeof registeredUsersSchema>

/** The two-way split, in the order the card renders it. */
export function userTypeBreakdown(
	users: RegisteredUsers,
): readonly { userType: UserType; count: number }[] {
	return USER_TYPES.map(userType => ({ userType, count: users.byUserType[userType] }))
}

// -----------------------------------------------------------------------------
// The summary, and the six cards read out of it
// -----------------------------------------------------------------------------

/**
 * The window the figures were computed over, as the service resolved it.
 *
 * The client sends a SELECTION — `yesterday`, `last_week`, a custom pair of
 * calendar dates — and the server evaluates the day boundaries in Asia/Kolkata
 * (US-01-006.2/.19). `label` is what the cards show, so the label and the
 * arithmetic cannot disagree; `timezone` is echoed so a card can say which zone
 * a date is being read in.
 */
export const reportingWindowSchema = z.object({
	kind: z.string(),
	label: z.string(),
	timezone: z.string().default("Asia/Kolkata"),
})

export type ReportingWindow = z.infer<typeof reportingWindowSchema>

/**
 * Everything the dashboard renders, in one response.
 *
 * EVERY SECTION IS OPTIONAL. Not because the service omits them — it sends all
 * six — but because a section this client cannot read must cost one card rather
 * than the page: the selectors below answer null and the affected card shows its
 * error state while the rest render (US-01-006.17).
 */
export const reportingSummarySchema = z.object({
	window: reportingWindowSchema,
	queries: z
		.object({
			total: z.number().int().nonnegative(),
			byStatus: statusCountsSchema,
		})
		.optional(),
	responseTime: z
		.object({
			last7Days: responseTimeFigureSchema,
			last30Days: responseTimeFigureSchema,
		})
		.optional(),
	vetting: z
		.object({
			total: z.number().int().nonnegative(),
			byStatus: statusCountsSchema,
		})
		.optional(),
	satisfaction: z
		.object({
			hasData: z.boolean(),
			responses: z.number().int().nonnegative(),
			satisfiedPercent: z.number().nullish(),
			notSatisfiedPercent: z.number().nullish(),
		})
		.optional(),
	categories: z
		.array(
			z.object({
				category: queryCategorySchema,
				label: z.string().optional(),
				count: z.number().int().nonnegative(),
			}),
		)
		.optional(),
	registeredUsers: z
		.object({
			scope: z.string().default("all_time"),
			total: z.number().int().nonnegative(),
			individual: z.number().int().nonnegative(),
			professional: z.number().int().nonnegative(),
		})
		.optional(),
	/** When the figures were computed, for the 'as of' line (US-01-006.19). */
	generatedAt: z.string().optional(),
})

export type ReportingSummary = z.infer<typeof reportingSummarySchema>

/** Total queries and the four-way status split (US-01-006.1/.4). */
export function queryVolumeOf(summary: ReportingSummary): QueryVolume | null {
	if (!summary.queries) return null
	return {
		totalQueries: summary.queries.total,
		statusCounts: summary.queries.byStatus,
		windowLabel: summary.window.label,
	}
}

/** Vetting totals and their status split (US-01-006.7). */
export function vettingVolumeOf(summary: ReportingSummary): VettingVolume | null {
	if (!summary.vetting) return null
	return {
		total: summary.vetting.total,
		statusCounts: summary.vetting.byStatus,
		windowLabel: summary.window.label,
	}
}

/**
 * Satisfaction, with the denominator (US-01-006.8).
 *
 * `hasData: false` becomes NULL percentages rather than zeros, because the card
 * has to show 'no feedback yet' and not "0% satisfied" — which is a different
 * and much worse statement to put in front of a Panel Lead.
 */
export function satisfactionOf(summary: ReportingSummary): Satisfaction | null {
	if (!summary.satisfaction) return null
	const { hasData, responses, satisfiedPercent, notSatisfiedPercent } = summary.satisfaction
	return {
		responseCount: responses,
		satisfiedPercent: hasData ? (satisfiedPercent ?? null) : null,
		notSatisfiedPercent: hasData ? (notSatisfiedPercent ?? null) : null,
		windowLabel: summary.window.label,
	}
}

/** Query volume by category — all seven, zero-filled by `categorySeries`. */
export function categoryVolumeOf(summary: ReportingSummary): CategoryVolume | null {
	if (!summary.categories) return null
	return {
		counts: summary.categories.map(entry => ({
			category: entry.category,
			count: entry.count,
		})),
		windowLabel: summary.window.label,
	}
}

/** Registered users, all-time regardless of the window (US-01-006.11). */
export function registeredUsersOf(summary: ReportingSummary): RegisteredUsers | null {
	if (!summary.registeredUsers) return null
	return {
		total: summary.registeredUsers.total,
		byUserType: {
			professional: summary.registeredUsers.professional,
			individual: summary.registeredUsers.individual,
		},
		scope: summary.registeredUsers.scope,
	}
}

/** The trailing 7-day and 30-day averages (US-01-006.5/.6). */
export function responseTimesOf(summary: ReportingSummary): ResponseTimes | null {
	return summary.responseTime ?? null
}
